import React, { useEffect, useState, useRef, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { FileWithPath, useDropzone } from "react-dropzone";
import { FileDetails } from "@/types/types";
import { UploadFileToS3 } from "@/utils/uploadFilestoS3";
import { Plus, ChevronDown, Trash2 } from "lucide-react";
import UploadOrange from "@/assets/icons/authentication/UploadOrange.svg";
import toast from "react-hot-toast";

// ─── Tag types ────────────────────────────────────────────────────────────────
type TOutfitTag = "formal" | "semiformal" | "casual" | "traditional" | "other";
type TBackgroundTag = "cleanwall" | "couch" | "indoor" | "outdoor" | "other";
type TPhotoTag = TOutfitTag | TBackgroundTag;

const OUTFIT_TAG_OPTIONS: { label: string; value: TOutfitTag }[] = [
  { label: "Formal", value: "formal" },
  { label: "Semi-formal", value: "semiformal" },
  { label: "Casual", value: "casual" },
  { label: "Traditional", value: "traditional" },
  { label: "Other", value: "other" },
];

const BACKGROUND_TAG_OPTIONS: { label: string; value: TBackgroundTag }[] = [
  { label: "Clean Wall", value: "cleanwall" },
  { label: "Couch", value: "couch" },
  { label: "Indoor", value: "indoor" },
  { label: "Outdoor", value: "outdoor" },
  { label: "Other", value: "other" },
];

// ─── Types ────────────────────────────────────────────────────────────────────
type TaggedFile = FileDetails & {
  tag?: TPhotoTag;
  otherTagText?: string;
  uploading?: boolean;
  uploadError?: string | null;
};

interface UploadOutfitBackgroundProps {
  mode?: "outfit" | "background";
  useExternalState?: boolean;
  externalFiles?: TaggedFile[];
  onExternalFilesChange?: (files: TaggedFile[]) => void;
  useLiveUrl?: boolean;
  maxSize?: number;
  accept?: { [key: string]: string[] };
  clearFiles?: boolean;
  maxFiles?: number;
  onUploadingChange?: (uploading: boolean) => void;
}

// ─── Portal Dropdown ──────────────────────────────────────────────────────────
// Renders the panel via createPortal into document.body — completely escapes
// any parent overflow:hidden or z-index stacking context.
const TagDropdown = ({
  options,
  value,
  onChange,
}: {
  options: { label: string; value: string }[];
  value: string;
  onChange: (val: string) => void;
}) => {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [panelStyle, setPanelStyle] = useState<React.CSSProperties>({});

  // Recompute panel position whenever it opens
  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setPanelStyle({
      position: "fixed",
      top: rect.bottom + 6,
      left: rect.right - 130, // right-align panel to trigger
      minWidth: 130,
      zIndex: 9999,
    });
  }, [open]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!triggerRef.current?.contains(t) && !panelRef.current?.contains(t)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // Close if user scrolls (panel would drift from trigger)
  useEffect(() => {
    if (!open) return;
    const handler = () => setOpen(false);
    window.addEventListener("scroll", handler, true);
    return () => window.removeEventListener("scroll", handler, true);
  }, [open]);

  const selected = options.find((o) => o.value === value);

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((p) => !p)}
        className={`
          flex items-center gap-1.5 bg-white px-2.5 py-1 rounded-full
          border text-[10px] font-medium transition-all duration-200 outline-none
          ${
            open
              ? "border-[#F97316] shadow-[0_0_0_2px_rgba(249,115,22,0.15)] text-[#F97316]"
              : "border-[#D0D5DD] text-[#667085] hover:border-[#F97316] hover:text-[#F97316]"
          }
        `}
      >
        <span className="max-w-[72px] truncate">
          {selected?.label ?? "Tag"}
        </span>
        <ChevronDown
          size={11}
          strokeWidth={2.5}
          className={`shrink-0 transition-transform duration-200 ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>

      {open &&
        createPortal(
          <div
            ref={panelRef}
            style={panelStyle}
            className="bg-white rounded-xl border border-[#E5E7EB]
                       shadow-[0_8px_24px_rgba(0,0,0,0.12)] overflow-hidden py-1"
          >
            {options.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => {
                  onChange(opt.value);
                  setOpen(false);
                }}
                className={`
                  w-full flex items-center justify-between px-3 py-1.5
                  text-[12px] text-left transition-colors duration-150
                  ${
                    value === opt.value
                      ? "bg-[#FFF7F0] text-[#F97316]"
                      : "text-[#374151] hover:bg-[#F9FAFB]"
                  }
                `}
              >
                <span>{opt.label}</span>
                {value === opt.value && (
                  <span className="w-1.5 h-1.5 rounded-full bg-[#F97316] shrink-0" />
                )}
              </button>
            ))}
          </div>,
          document.body,
        )}
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const UploadOutfitBackground: React.FC<UploadOutfitBackgroundProps> = ({
  mode = "outfit",
  useExternalState = false,
  externalFiles = [],
  onExternalFilesChange,
  useLiveUrl = true,
  maxFiles = 4,
  maxSize = 6 * 1024 * 1024,
  accept = { "image/jpeg": [], "image/png": [], "image/jpg": [] },
  clearFiles = false,
  onUploadingChange,
}) => {
  const [localFiles, setLocalFiles] = useState<TaggedFile[]>([]);
  const [uploading, setUploading] = useState(false);

  const tagOptions =
    mode === "background" ? BACKGROUND_TAG_OPTIONS : OUTFIT_TAG_OPTIONS;
  const defaultTag: TPhotoTag = mode === "background" ? "cleanwall" : "formal";

  const files = useExternalState ? externalFiles : localFiles;
  const filesRef = useRef<TaggedFile[]>(files);
  const setFiles = useExternalState
    ? onExternalFilesChange
    : (next: TaggedFile[]) => setLocalFiles(next);

  useEffect(() => {
    filesRef.current = files;
  }, [files]);

  useEffect(() => {
    onUploadingChange?.(uploading);
  }, [onUploadingChange, uploading]);

  const commitFiles = (next: TaggedFile[]) => {
    filesRef.current = next;
    setFiles?.(next);
  };

  const updateFile = (name: string, patch: Partial<TaggedFile>) => {
    commitFiles(
      filesRef.current.map((file) =>
        file.name === name ? { ...file, ...patch } : file,
      ),
    );
  };

  useEffect(() => {
    if (clearFiles && setFiles) commitFiles([]);
  }, [clearFiles]);

  const canAddMore = files.length < maxFiles;

  const onDrop = async (acceptedFiles: FileWithPath[]) => {
    if (!setFiles || uploading) return;
    if (!acceptedFiles?.length) return;
    const remaining = maxFiles - files.length;
    const incoming = acceptedFiles.slice(0, remaining);
    if (!incoming.length) return;

    setUploading(true);
    const sanitizeFileName = (name: string) =>
      name.replace(/[^a-zA-Z0-9.\-_]/g, "_").slice(0, 100);

    const now = Date.now();
    const localItems: TaggedFile[] = incoming.map((file, idx) => ({
      name: `${now + idx}-${sanitizeFileName(file.name)}`,
      size: file.size,
      type: file.type,
      lastModified: file.lastModified,
      webkitRelativePath: file.webkitRelativePath,
      local_url: URL.createObjectURL(file),
      file,
      live_url: null,
      uploading: true,
      tag: defaultTag,
      otherTagText: "",
    }));

    commitFiles([...filesRef.current, ...localItems]);

    try {
      await Promise.all(localItems.map(uploadFile));
    } finally {
      setUploading(false);
    }
  };

  const uploadFile = async (item: TaggedFile) => {
    updateFile(item.name, { uploading: true, uploadError: null });

    try {
      const res = await UploadFileToS3(item, undefined, {
        timeoutMs: 120_000,
      });
      updateFile(item.name, {
        live_url: res.live_url,
        local_url: res.live_url as string,
        file: undefined,
        size: res.size,
        type: res.type,
        uploading: false,
        uploadError: null,
      });
      URL.revokeObjectURL(item.local_url);
      return;
    } catch (error) {
      console.error(`Failed to upload file: ${item.name}`, error);
      updateFile(item.name, {
        uploading: false,
        uploadError: error instanceof Error ? error.message : "Upload failed",
      });
      toast.error("An image failed to upload. Tap Retry on the image.", {
        id: `image-upload-${mode}`,
      });
    }
  };

  const retryFile = async (item: TaggedFile) => {
    if (uploading) return;
    setUploading(true);
    try {
      await uploadFile(item);
    } finally {
      setUploading(false);
    }
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    multiple: true,
    maxSize,
    accept,
    onDropRejected: (rejections) => {
      toast.error(
        rejections[0]?.errors[0]?.message || "Please select a supported image.",
      );
    },
    disabled: !canAddMore || uploading,
  });

  const removeFile = (name: string) => {
    if (!setFiles) return;
    const removed = filesRef.current.find((file) => file.name === name);
    if (removed?.uploading) return;
    if (removed?.local_url?.startsWith("blob:")) {
      URL.revokeObjectURL(removed.local_url);
    }
    commitFiles(filesRef.current.filter((file) => file.name !== name));
  };

  const setTag = (name: string, tag: TPhotoTag) => {
    if (!setFiles) return;
    commitFiles(
      filesRef.current.map((f) =>
        f.name === name
          ? {
              ...f,
              tag,
              otherTagText: tag === "other" ? f.otherTagText ?? "" : "",
            }
          : f,
      ),
    );
  };

  const setOtherText = (name: string, text: string) => {
    if (!setFiles) return;
    commitFiles(
      filesRef.current.map((f) =>
        f.name === name ? { ...f, otherTagText: text } : f,
      ),
    );
  };

  const getSrc = (f: TaggedFile) =>
    !f.live_url
      ? f.local_url
      : useLiveUrl
        ? (f.live_url as string)
        : (f.local_url as string);

  return (
    <div className="mt-3">
      {canAddMore && (
        <div {...getRootProps()} className="w-fit cursor-pointer select-none">
          <input {...getInputProps()} />
          <div className="border-dashed border border-[#CDCDCD] h-[111px] w-[107px] flex flex-col justify-center items-center rounded-xl bg-white">
            <Plus color="#CDCDCD" size={35} />
            <div className="border border-[#EEEEEE] rounded-lg py-1 px-[9.5px] flex items-center gap-x-1 mt-1 bg-white">
              <UploadOrange />
              <p className="text-[#344054] text-[10px] tracking-normal font-medium">
                Upload
              </p>
            </div>
          </div>
        </div>
      )}

      {files.length > 0 && (
        <div className="mt-5 flex flex-wrap gap-3">
          {files.map((f) => {
            const currentTag = f.tag ?? defaultTag;
            return (
              <div key={f.name} className="relative w-fit">
                {/* overflow-hidden is safe here now — dropdown escapes via portal */}
                <div className="bg-[#EEEEEE] h-[137px] w-[133px] rounded-xl relative overflow-hidden">
                  <img
                    src={getSrc(f)}
                    alt={f.name}
                    className="absolute inset-0 object-cover w-full h-full"
                  />

                  {f.uploading && (
                    <div className="absolute inset-0 bg-white/60 flex items-center justify-center text-[10px] font-medium text-[#475467] z-10">
                      Uploading...
                    </div>
                  )}

                  {f.uploadError && !f.uploading && (
                    <div className="absolute inset-0 bg-white/75 flex flex-col items-center justify-center gap-2 z-20">
                      <span className="text-[10px] font-medium text-red-600">
                        Upload failed
                      </span>
                      <div className="flex gap-1.5">
                        <button
                          type="button"
                          onClick={() => retryFile(f)}
                          className="rounded-full bg-[#F97316] px-3 py-1 text-[10px] font-semibold text-white"
                        >
                          Retry
                        </button>
                        <button
                          type="button"
                          onClick={() => removeFile(f.name)}
                          className="rounded-full border border-[#D0D5DD] bg-white px-3 py-1 text-[10px] font-semibold text-[#475467]"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  )}

                  <div className="absolute top-2 right-2 z-10">
                    <TagDropdown
                      options={tagOptions}
                      value={currentTag}
                      onChange={(val) => setTag(f.name, val as TPhotoTag)}
                    />
                  </div>

                  <button
                    type="button"
                    onClick={() => removeFile(f.name)}
                    disabled={f.uploading}
                    className="absolute bottom-2 left-2 z-10 flex items-center gap-x-[6px] bg-white rounded-lg py-1 px-2 border border-[#EAECF0] hover:bg-red-50 hover:border-red-200 transition-colors duration-150"
                  >
                    <Trash2 size={12} className="text-[#667085]" />
                    <p className="text-[10px] tracking-[-1%] text-[#101828]">
                      Remove
                    </p>
                  </button>
                </div>

                {currentTag === "other" && (
                  <input
                    value={f.otherTagText ?? ""}
                    onChange={(e) => setOtherText(f.name, e.target.value)}
                    placeholder={
                      mode === "background"
                        ? "e.g. Studio, Rooftop..."
                        : "e.g. Streetwear, Sports..."
                    }
                    className="mt-2 w-[133px] h-8 px-2.5 text-[10px] border border-[#E5E7EB]
                               rounded-lg outline-none focus:border-[#F97316]
                               focus:shadow-[0_0_0_2px_rgba(249,115,22,0.1)]
                               transition-all duration-200 placeholder-[#C1C9D2]"
                  />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default UploadOutfitBackground;
